import { Subject } from 'rxjs';
import { cartService } from './service';
import { debounce } from '../../shared/debounce';

export class CartStore {
  constructor(rootStore) {
    this.rootStore = rootStore;
    this.subject = new Subject();
    this.cart = {};
    // Монотонный счётчик запросов: ответ применяем только если после него не
    // отправлялись более новые запросы (иначе устаревший ответ откатит UI).
    this._reqSeq = 0;
    // item_id товара, чья отправка количества сейчас ждёт в debounce
    // (нужен, чтобы отменить её при удалении этой позиции)
    this._pendingProductItemId = null;
    // последнее подтверждённое сервером need_coupled_count (для отката при ошибке)
    this._tareSavedCount = null;
    // debounced-вариант серверной синхронизации количества товара.
    // Ошибку глушим: откат и emit уже сделал _sendProduct, а await-ить
    // отложенный вызов некому.
    this._sendProductDebounced = debounce((payload, prev) => {
      this._pendingProductItemId = null;
      this._sendProduct(payload, prev).catch(() => {});
    }, 600);
    // Дебаунс отправки `update_tare_promise` — на уровне стора, как в мобилке
    // (`client_app/src/stores/cartStore/index.ts`). Ошибка обработана внутри
    // _sendTarePromise (откат значения), наружу пробрасывать некому.
    this._sendTarePromiseDebounced = debounce((count) => this._sendTarePromise(count).catch(() => {}), 600);
  }

  // Мобильный паттерн `changeNeedCoupledCount`: оптимистично пишем число в
  // корзину, эмитим подписчикам, затем debounce-шлём на сервер.
  changeNeedCoupledCount(count) {
    // запоминаем последнее подтверждённое сервером значение — для отката при ошибке
    if (this._tareSavedCount == null) this._tareSavedCount = this.cart.need_coupled_count || 0;
    this.cart.need_coupled_count = count;
    this._emit();
    this._sendTarePromiseDebounced(count);
  }

  async _sendTarePromise(count) {
    const seq = ++this._reqSeq;
    try {
      const cart = await cartService.changeReturnTarePromiseCount(count);
      const local = this.cart.need_coupled_count;
      if (local !== count) {
        // пока запрос летел, пользователь снова менял счётчик — не даём
        // устаревшему ответу откатить более новое локальное значение
        // (его отправит уже взведённый debounce)
        cart.need_coupled_count = local;
        this._tareSavedCount = count; // сервер подтвердил именно count
      } else {
        this._tareSavedCount = null;
      }
      this._applyCart(cart, seq);
    } catch (e) {
      // синхронизация не удалась — возвращаем последнее подтверждённое значение
      if (this._tareSavedCount != null && this.cart.need_coupled_count === count) {
        this.cart.need_coupled_count = this._tareSavedCount;
        this._tareSavedCount = null;
        this._emit();
      }
      throw e;
    }
  }

  _emit() { this.subject.next(this); }

  setCart(cart) { this.cart = cart || {}; this._emit(); }

  // Применяем ответ сервера, только если это ответ на самый свежий запрос.
  _applyCart(cart, seq) {
    if (seq !== this._reqSeq) return;
    this.setCart(cart);
  }

  // Снимок корзины для отката оптимистичных изменений (позиции копируются,
  // т.к. quantity мутируется на месте).
  _snapshot() {
    return Object.assign({}, this.cart, { items: this.items.map((i) => Object.assign({}, i)) });
  }

  get items() { return this.cart.items || []; }
  get count() { return this.cart.count || 0; }
  get totalSum() { return this.cart.total_sum || 0; }
  get needCoupledCount() { return this.cart.need_coupled_count || 0; }
  // Выбранный режим тары; null, пока клиент ничего не выбирал.
  get tareMode() { return this.cart.tare_mode || null; }

  get needTareAsk() {
    return !!(this.cart.first_order && (this.cart.associated_coupled_items_count || 0) > 0 && (this.cart.need_coupled_count || 0) > 0);
  }

  async addProductToCart(payload) {
    const items = this.items;
    const existing = items.find((i) => i.item_id === payload.item_id);
    const prev = this._snapshot();
    if (existing) {
      // оптимистично обновляем количество в локальной корзине, debounce-им отправку
      existing.quantity = payload.quantity;
      this._emit();
      this._pendingProductItemId = payload.item_id;
      this._sendProductDebounced(payload, prev);
    } else {
      // первое добавление — тоже оптимистично: позиция и счётчик в шапке
      // реагируют сразу, сервер подтверждает следом (и присылает полную корзину)
      this.setCart(Object.assign({}, this.cart, {
        items: items.concat([Object.assign({}, payload)]),
        count: (this.cart.count || 0) + 1,
        total_quantity: (this.cart.total_quantity || 0) + (payload.quantity || 0),
      }));
      await this._sendProduct(payload, prev);
    }
  }

  async _sendProduct(payload, prev) {
    const seq = ++this._reqSeq;
    try {
      const cart = await cartService.addProductToCart(payload);
      this._applyCart(cart, seq);
    } catch (e) {
      // сервер не принял изменение — откатываем оптимистичную мутацию
      if (prev) this._applyCart(prev, seq);
      throw e;
    }
  }

  async removeProductFromCart(itemId, toppingIds) {
    // отменяем отложенную отправку количества этой же позиции — иначе её
    // устаревший ответ «воскресит» только что удалённый товар
    if (this._pendingProductItemId === itemId) {
      this._sendProductDebounced.cancel();
      this._pendingProductItemId = null;
    }
    const prev = this._snapshot();
    // оптимистично выкидываем из локальной корзины
    const filtered = this.items.filter((i) => i.item_id !== itemId);
    this.setCart(Object.assign({}, this.cart, { items: filtered }));
    const seq = ++this._reqSeq;
    try {
      const cart = await cartService.addProductToCart({ item_id: itemId, quantity: 0, topping_ids: toppingIds });
      this._applyCart(cart, seq);
    } catch (e) {
      // удаление не прошло — возвращаем позицию на место
      this._applyCart(prev, seq);
      throw e;
    }
  }

  async cleanCart() {
    // отложенные отправки количества больше не актуальны
    this._sendProductDebounced.cancel();
    this._pendingProductItemId = null;
    // оптимистично очищаем локально, при ошибке возвращаем как было
    const prev = this._snapshot();
    this.setCart({});
    const seq = ++this._reqSeq;
    try {
      const cart = await cartService.cleanCart();
      this._applyCart(cart || {}, seq);
      return cart;
    } catch (e) {
      this._applyCart(prev, seq);
      throw e;
    }
  }

  async addCoupled() {
    const seq = ++this._reqSeq;
    const cart = await cartService.addCoupled();
    this._applyCart(cart, seq);
    return cart;
  }

  // Способ получения тары. Оптимистично помечаем выбор, чтобы радио
  // переключалось сразу; состав тарных строк и сумму пересобирает сервер и
  // присылает готовую корзину. При ошибке (422 — режим недоступен) сервер
  // корзину не менял, поэтому возвращаем локальный снапшот.
  async setTareMode(mode) {
    const prev = this._snapshot();
    this.cart.tare_mode = mode;
    this._emit();
    const seq = ++this._reqSeq;
    try {
      const cart = await cartService.setTareMode(mode);
      this._applyCart(cart, seq);
      return cart;
    } catch (e) {
      this._applyCart(prev, seq);
      throw e;
    }
  }

  async repeatOrder(orderId) {
    const seq = ++this._reqSeq;
    const cart = await cartService.repeatOrder(orderId);
    this._applyCart(cart, seq);
  }
}



// WEBPACK FOOTER //
// ./app/javascript/website_newgen/js/stores/cartStore/index.js